import React from "react";
import { BaseCard, SVGIcon } from "../baseCard/baseCard.tsx";
import {
    getFontSizes,
    getTitleHeight,
    darkenColor,
} from "../baseCard/BaseCardUtils.ts";
import "./PeopleCard.css";

interface PeopleCardProps {
    category: string;
    title: string;
    subtitle1: string;
    score1: number; // 1-5
    subtitle2: string;
    score2: number; // 1-5
    subtitle3?: string;
    score3?: number;
    highlight?: boolean;
}

const PeopleCard: React.FC<PeopleCardProps> = ({
    category,
    title,
    subtitle1,
    score1,
    subtitle2,
    score2,
    subtitle3,
    score3,
    highlight = false,
}) => {
    const fontSizes = getFontSizes(highlight);
    const titleHeight = getTitleHeight(highlight);
    const color = "var(--color-light-gray)";
    const shapeColor = darkenColor(color);

    const renderDots = (score: number) => {
        const dots = [];
        for (let i = 0; i < 10; i++) {
            dots.push(
                <div>{(i < score / 10) ?
                    <SVGIcon
                        className="PeopleCard-icon"
                        viewBox="0 0 24 24"
                        fillColor={"#22ad7a"}
                        path={svgPaths.personFilled}
                    />

                    :
                    <SVGIcon
                        className="PeopleCard-icon"
                        viewBox="0 0 24 24"
                        fillColor={"#BABABA"}
                        path={svgPaths.person}
                    />
                } </div>

            );
        }
        return dots;
    };

    const svgPaths = {
        topShape:
            "M20.7637 1.11044e-05C9.71799 1.24175e-05 0.76368 8.95433 0.763673 20L0.763678 97.4131C9.37694 104.498 32.5399 112.037 67.7002 90.1162C103.94 67.5225 112.736 17.7127 114.172 0L20.7637 1.11044e-05Z",
        bottomShape:
            "M206.493 109.598C206.493 120.643 197.539 129.598 186.493 129.598H0.626953C8.79625 105.382 42.9206 56.9502 114.063 56.9502C185.206 56.9502 205.326 19.2817 206.493 0.447266V109.598Z",
        person:
            "M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5m0-8c1.65 0 3 1.35 3 3s-1.35 3-3 3-3-1.35-3-3 1.35-3 3-3M4 22h16c.55 0 1-.45 1-1v-1c0-3.86-3.14-7-7-7h-4c-3.86 0-7 3.14-7 7v1c0 .55.45 1 1 1m6-7h4c2.76 0 5 2.24 5 5H5c0-2.76 2.24-5 5-5",
        personFilled:
            "M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5m0-8c1.65 0 3 1.35 3 3s-1.35 3-3 3-3-1.35-3-3 1.35-3 3-3M4 22h16c.55 0 1-.45 1-1v-1c0-3.86-3.14-7-7-7h-4c-3.86 0-7 3.14-7 7v1c0 .55.45 1 1 1m6-7h4c2.76 0 5 2.24 5 5H5c0-2.76 2.24-5 5-5",



    }


    return (
        <BaseCard highlight={highlight} color={color} className="PeopleCard-card">
            <SVGIcon
                className="baseCard-icon-top-right"
                viewBox="0 0 115 105"
                fillColor={shapeColor}
                path={svgPaths.topShape}
            />

            <SVGIcon
                className="baseCard-icon-bottom-left"
                viewBox="0 0 207 130"
                fillColor={shapeColor}
                path={svgPaths.bottomShape}
            />

            <div className="baseCard-content PeopleCard-content">
                <div className="PeopleCard-header">
                    <div
                        className="PeopleCard-category"
                        style={{ fontSize: fontSizes.category }}
                    >
                        {category.toUpperCase()}
                    </div>

                    <div
                        className="PeopleCard-title"
                        style={{
                            fontSize: fontSizes.title,
                            height: titleHeight,
                        }}
                    >
                        {title}
                    </div>

                    <div
                        className="baseCard-divider PeopleCard-divider"
                        style={{
                            width: "calc(100% + 2 * var(--spacing-3))",
                        }}
                    ></div>
                </div>

                <div className="PeopleCard-main-content">
                    <div className="PeopleCard-score-section">
                        <div className="PeopleCard-score-header">
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {subtitle1}
                            </span>
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {score1}{"%"}
                            </span>
                        </div>

                        <div className="PeopleCard-dots-container">{renderDots(score1)}</div>
                        <div className="PeopleCard-thin-divider"></div>
                    </div>
                    <div className="PeopleCard-score-section">
                        <div className="PeopleCard-score-header">
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {subtitle2}
                            </span>
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {score2}{"%"}
                            </span>
                        </div>

                        <div className="PeopleCard-dots-container">{renderDots(score2)}</div>

                    </div>
                    <div className="PeopleCard-thin-divider"></div>
                    <div className="PeopleCard-score-section">
                        <div className="PeopleCard-score-header">
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {subtitle3}
                            </span>
                            <span
                                className="PeopleCard-subtitle2"
                                style={{ fontSize: fontSizes.listItem }} >
                                {score3}{"%"}
                            </span>
                        </div>

                        <div className="PeopleCard-dots-container">{renderDots(score3 || 0)}</div>

                    </div>
                </div>
            </div>
        </BaseCard>
    );
};

export default PeopleCard
